import { useState, useEffect, useRef } from 'react';
import { useStoreContext } from '../context/StoreContext';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.dilzee.com';

export default function ChatWidget() {
  const { subdomain, store } = useStoreContext();
  const settings = store?.settings || {};
  
  const [isOpen, setIsOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(false);
  const [storeId, setStoreId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState('');
  const [stage, setStage] = useState<'intro' | 'form' | 'chat'>('intro');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check chat status
  useEffect(() => {
    if (!subdomain) return;
    fetch(`${API_URL}/chat/status/${subdomain}`)
      .then(r => r.json())
      .then(data => { 
        setIsOnline(data.online); 
        setChatEnabled(data.enabled); 
        setStoreId(data.store_id || ''); 
      })
      .catch(() => {});
  }, [subdomain]);

  // Connect to WebSocket when chat is active
  const [ws, setWs] = useState<WebSocket | null>(null);

  useEffect(() => {
    if (!sessionId || !isOpen) return;

    let socket: WebSocket;
    const connect = () => {
      const wsUrl = `${API_URL.replace('http', 'ws')}/chat/ws?type=customer&store_id=${storeId}&session_id=${sessionId}`;
      socket = new WebSocket(wsUrl);

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            setMessages(prev => {
              // Deduplicate by checking for optimistic message (no ID, same content/sender)
              const existingIndex = prev.findIndex(m => !m.id && m.content === data.message.content && m.sender === data.message.sender);
              if (existingIndex >= 0) {
                const newMessages = [...prev];
                newMessages[existingIndex] = data.message;
                return newMessages;
              }
              // Check if we already received this ID
              if (prev.some(m => m.id && m.id === data.message.id)) return prev;
              
              return [...prev, data.message];
            });
          }
        } catch {}
      };

      socket.onclose = () => {
        setTimeout(connect, 3000);
      };
      
      setWs(socket);
    };

    connect();

    return () => {
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
    };
  }, [sessionId, isOpen, storeId]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  if (!chatEnabled) return null;

  const startChat = async () => {
    if (!customerName.trim()) return;
    const res = await fetch(`${API_URL}/chat/start`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store_id: storeId, customer_name: customerName, customer_email: customerEmail, first_message: `Hi! I need help.` }),
    }).then(r => r.json());
    if (res.session_id) {
      setSessionId(res.session_id);
      setMessages([{ sender: 'customer', content: 'Hi! I need help.', created_at: Math.floor(Date.now()/1000) }]);
      if (isOnline) {
        setMessages(prev => [...prev, { sender: 'merchant', content: `Hello ${customerName}! How can I help you today?`, created_at: Math.floor(Date.now()/1000) + 1 }]);
      } else {
        setMessages(prev => [...prev, { sender: 'merchant', content: `Hi ${customerName}! We're currently offline. Leave your message and we'll get back to you soon.`, created_at: Math.floor(Date.now()/1000) + 1 }]);
      }
      setStage('chat');
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !sessionId) return;
    const text = input;
    setInput('');
    const newMsg = { sender: 'customer', content: text, created_at: Math.floor(Date.now()/1000) };
    setMessages(prev => [...prev, newMsg]);
    
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'send', session_id: sessionId, store_id: storeId, content: text, sender: 'customer' }));
    } else {
      await fetch(`${API_URL}/chat/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, store_id: storeId, content: text, sender: 'customer' }),
      }).catch(() => {});
    }
  };

  return (
    <>
      {/* Floating button */}
      <button onClick={() => setIsOpen(o => !o)}
        style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 999,
          width: '56px', height: '56px', borderRadius: '50%',
          background: 'var(--accent, #fd3c02)', border: 'none', cursor: 'pointer',
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
        {isOpen
          ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-contrast, #121212)" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
          : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--accent-contrast, #121212)" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        }
        {isOnline && !isOpen && (
          <span style={{ position: 'absolute', top: '2px', right: '2px', width: '12px', height: '12px', background: '#22c55e', borderRadius: '50%', border: '2px solid var(--surface-card, white)' }} />
        )}
      </button>

      <style>{`
        html[data-viewport="mobile"] button[style*="bottom: 24px"][style*="right: 24px"] {
          right: calc(50vw - 195px + 24px) !important;
        }
      `}</style>
      
      {/* Chat window */}
      {isOpen && (
        <div style={{
          position: 'fixed', bottom: '92px', right: '24px', zIndex: 998,
          width: '340px', height: '480px',
          background: 'var(--surface-card, white)', color: 'var(--text, #1a1a1a)', borderRadius: '16px',
          border: '1px solid var(--border)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ background: 'var(--accent, #fd3c02)', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            {settings?.logo_url ? <img src={settings.logo_url} alt="" height="32" style={{ borderRadius: '50%', objectFit: 'cover', width: '32px' }} />
              : <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-contrast, #121212)', fontWeight: 700 }}>{store?.store_name?.[0]}</div>}
            <div>
              <div style={{ color: 'var(--accent-contrast, #121212)', fontWeight: 600, fontSize: '14px' }}>{store?.store_name}</div>
              <div style={{ color: 'var(--accent-contrast, #121212)', opacity: 0.8, fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isOnline ? '#22c55e' : '#f59e0b', display: 'inline-block' }} />
                {isOnline ? 'Online — reply in minutes' : 'Offline — leave a message'}
              </div>
            </div>
          </div>

          {/* Intro stage */}
          {stage === 'intro' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>👋</div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: 'var(--text)' }}>Hi there!</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted, #6b7280)', marginBottom: '24px', lineHeight: 1.6 }}>
                {isOnline ? 'We\'re online and ready to help. Start a chat!' : 'We\'re away right now, but leave a message and we\'ll get back to you.'}
              </p>
              <button onClick={() => setStage('form')}
                style={{ background: 'var(--accent, #fd3c02)', color: 'var(--accent-contrast, #121212)', border: 'none', padding: '12px 28px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, width: '100%' }}>
                Start conversation
              </button>
            </div>
          )}

          {/* Form stage */}
          {stage === 'form' && (
            <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ fontSize: '13px', color: 'var(--text-muted, #6b7280)' }}>Just a couple of details before we start:</p>
              <input value={customerName} onChange={e => setCustomerName(e.target.value)}
                placeholder="Your name *" style={{ height: '40px', border: '1px solid var(--border, #e5e7eb)', borderRadius: '8px', padding: '0 14px', fontSize: '13px', width: '100%', background: 'var(--surface, white)', color: 'var(--text)' }} />
              <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)}
                placeholder="Email (optional)" style={{ height: '40px', border: '1px solid var(--border, #e5e7eb)', borderRadius: '8px', padding: '0 14px', fontSize: '13px', width: '100%', background: 'var(--surface, white)', color: 'var(--text)' }} />
              <button onClick={startChat} disabled={!customerName.trim()}
                style={{ background: 'var(--accent, #fd3c02)', color: 'var(--accent-contrast, #121212)', border: 'none', padding: '12px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, opacity: customerName.trim() ? 1 : 0.5, marginTop: '8px' }}>
                Start chatting →
              </button>
            </div>
          )}

          {/* Chat stage */}
          {stage === 'chat' && (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {messages.map((msg, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: msg.sender === 'customer' ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '80%', padding: '10px 14px', borderRadius: msg.sender === 'customer' ? '16px 16px 4px 16px' : '4px 16px 16px 16px',
                      background: msg.sender === 'customer' ? 'var(--accent, #fd3c02)' : 'var(--surface, #f3f4f6)',
                      color: msg.sender === 'customer' ? 'var(--accent-contrast, #121212)' : 'var(--text, #1a1a1a)',
                      fontSize: '13px', lineHeight: 1.5,
                    }}>
                      {msg.content}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border, #f3f4f6)', display: 'flex', gap: '8px' }}>
                <input value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') sendMessage(); }}
                  placeholder="Type a message..."
                  style={{ flex: 1, height: '38px', border: '1px solid var(--border, #e5e7eb)', borderRadius: '8px', padding: '0 14px', fontSize: '13px', background: 'var(--surface, white)', color: 'var(--text)' }} />
                <button onClick={sendMessage} disabled={!input.trim()}
                  style={{ width: '38px', height: '38px', borderRadius: '50%', background: input.trim() ? 'var(--accent)' : 'var(--border, #e5e7eb)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-contrast, #121212)" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
